import {
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { ModelLoader } from "@/shared/components/visualization/Panel/ThreeDPanel/ModelLoader";
import { type FileRecord } from "@/shared/domain/files";
import type { File } from "@/shared/state/visualization/schema/v1/file";
import type {
  Joint,
  JointBinding,
} from "@/shared/state/visualization/schema/v1/three-d";

import { BabylonPreview } from "./BabylonPreview";
import { JointControls, type JointSectionMode } from "./JointControls";
import { PreviewSceneSettings } from "./PreviewSceneSettings";
import styles from "./PreviewStep.module.css";
import { SidebarResizeHandle } from "./SidebarResizeHandle";
import { PreviewSidebarStorage } from "./sidebarWidthStorage";
import { useModelPreview } from "./useModelPreview";

interface PreviewStepProps {
  /**
   * The generated glTF, or `undefined` if it could not be resolved.
   *
   * The caller only reaches this step once the conversion reports `completed`,
   * so by then the artifact is supposed to exist and be findable. `undefined`
   * therefore means something went wrong between the worker writing the file and
   * the app locating it — which is why it gets a stated error below rather than
   * an empty panel. Unless it has not been looked for yet: see {@link resolving}.
   */
  file: File | undefined;
  /**
   * Whether the artifact is still being located.
   *
   * `undefined` is not on its own an answer. The lookup that fills {@link file}
   * in is only started once the conversion reads `completed` — which is the
   * same render that first shows this step — so it *cannot* have answered by
   * then, and reading its silence as "there is no such file" puts a stated
   * error on screen for the length of a round trip every single time the
   * preview opens, which then vanishes. Only a lookup that has answered gets to
   * say the model is missing; one still in flight is a load.
   */
  resolving?: boolean;
  /**
   * References the converter dropped, carried forward from the conversion.
   * Non-empty means the model is missing geometry, and the user needs to know
   * which references it is missing before accepting the result.
   */
  unresolved: string[];
  /**
   * The **source** `.urdf` this asset was generated from.
   *
   * Only its org is read, and only to scope the topic catalogue each joint's
   * picker searches. The save it used to address is the dialog's now — see
   * {@link onBindingChange}.
   */
  sourceFile?: Pick<FileRecord, "file_id" | "org_id">;
  /**
   * What each joint is declared to be driven by, keyed by joint name.
   *
   * Supplied rather than derived, because the set this step *shows* is the set
   * the dialog's Save would write, and the two must be the same set. The model's
   * own declarations reach the owner through {@link onJointsLoaded}.
   */
  bindings?: Record<string, JointBinding>;
  /**
   * Edits one joint's declaration, or removes it with `undefined`.
   *
   * Its presence is what makes this an editor rather than a viewer: without an
   * owner holding a draft there is nothing to edit into and nowhere to save it,
   * so the mode control and the per-joint pickers are not offered at all.
   */
  onBindingChange?: (
    jointName: string,
    binding: JointBinding | undefined,
  ) => void;
  /**
   * Hands the loaded model's joints to the owner, so its draft can take what
   * the artifact already declares as its base.
   *
   * This is a *late* event, not a mount-time one: the glTF is fetched and parsed
   * after this component mounts, so the owner's draft is necessarily empty until
   * it fires. That is why the draft composes rather than seeds — an edit made
   * before the parse lands has to survive it.
   */
  onJointsLoaded?: (joints: readonly Joint[]) => void;
}

/**
 * The converted robot, in a Babylon viewport with a sidebar beside it holding
 * everything that edits the preview: the scene's viewpoint and scenery first,
 * then a row per joint.
 *
 * Knows only about a `PreviewScene`: a panel state to render and a list of
 * joints, with the sidebar posing them through a callback. How a glTF becomes
 * that scene — and what posing a joint does to the renderer — lives entirely in
 * `buildPreviewScene`.
 *
 * Slider positions are not persisted anywhere — this is a check that each joint
 * swings about the right axis, not an editor.
 *
 * Joint *bindings* are the one thing here that outlives the sitting, and this
 * step does not own them. It shows the set it is handed and reports each edit
 * back, because the button that commits them lives in the dialog's action row
 * alongside Cancel — where a user looks for the act that ends a wizard. A save
 * inside the panel, with a separate dismissal below it, is what that
 * arrangement replaced.
 */
export function PreviewStep({
  file,
  resolving = false,
  unresolved,
  sourceFile,
  bindings,
  onBindingChange,
  onJointsLoaded,
}: PreviewStepProps) {
  const { files } = useDomainServices();
  // One loader for both the scene build and the canvas, so the model is fetched
  // and parsed once and served from its template cache thereafter.
  const modelLoader = React.useMemo(() => new ModelLoader(files), [files]);
  const {
    scene,
    state,
    sceneConfig,
    setCamera,
    setGrid,
    isLoading,
    error,
    valueOf,
    setJoint,
    onManagerChanged,
  } = useModelPreview(modelLoader, file);

  /**
   * Hands the owner what the loaded glTF says each joint is driven by.
   *
   * The artifact's `ROBOTO_device` extension is the system of record for a
   * binding, and this preview has already parsed it to draw the robot — so
   * reading the declarations costs no fetch of its own. Reported only once a
   * scene exists: `undefined` is a model that has not been parsed yet, not a
   * model that declares nothing, and announcing the latter would tell the owner
   * every declaration had been dropped for the length of a fetch.
   */
  React.useEffect(() => {
    if (scene !== undefined) {
      onJointsLoaded?.(scene.joints);
    }
  }, [scene, onJointsLoaded]);

  /**
   * How much of each joint's row is on show.
   *
   * ## Why `bind`, always
   *
   * A fixed default, not one read off the model. The tempting rule — open on
   * `bind` for a robot that already declares bindings, on `pose` for a fresh
   * conversion — cannot be implemented honestly here: the declarations arrive
   * **late**, after the glTF has been fetched and parsed, so at mount there is
   * nothing to read and the only way to act on them is to change the mode out
   * from under a user who may already have chosen one. That is the same
   * late-arrival trap {@link onJointsLoaded} is shaped around, and a mode that
   * moves by itself is worse than either default.
   *
   * Of the two, `bind` is the one that shows *more*: the modes are additive, so
   * it is the sliders plus the binding editor. Defaulting to it means nothing
   * this step can do is hidden behind a control the user has not found yet, and
   * in particular that the person who opens the dialog and presses Save has
   * been shown what Save is about to write. `pose` is then a deliberate act of
   * *narrowing* — a twenty-joint robot read as a list of axes — which is the
   * right way round for a default and an opt-out.
   *
   * The control only exists when there is a draft to edit into
   * ({@link onBindingChange}); a read-only preview has no editor to show or
   * hide, and `JointControls` defaults to `pose` for it.
   */
  const [jointMode, setJointMode] = React.useState<JointSectionMode>("bind");

  /**
   * The sidebar, so the resize handle beside it has something to widen.
   *
   * Attached through a callback rather than read in a mount effect because the
   * column is not mounted at mount: it appears with `scene`, once the glTF has
   * been fetched and parsed, so an effect that ran on mount would find nothing
   * and the stored width would never be applied.
   */
  const sidebarRef = React.useRef<HTMLElement | null>(null);
  const attachSidebar = React.useCallback((node: HTMLElement | null) => {
    sidebarRef.current = node;
    if (node === null) {
      return;
    }
    const saved = PreviewSidebarStorage.getWidth();
    if (saved !== null) {
      // Gated by `min-width` / `max-width` in CSS, so a width saved on a wide
      // monitor cannot swallow the viewport on a laptop.
      node.style.width = `${saved}px`;
    }
  }, []);

  /**
   * Applies one drag step — or one arrow-key press — to the sidebar's width.
   *
   * Written straight onto the element rather than held in React state, which is
   * how the app's other two resizable sidebars do it and matters more here: a
   * state update per pointer move would re-render every joint row, each of
   * which carries a MUI `Autocomplete`, for a change that is one CSS property.
   *
   * The new width is measured from `offsetWidth` — the *laid-out* width, after
   * the CSS bounds have applied — so dragging past an edge does not bank up a
   * distance to be dragged back before the column moves again.
   *
   * The canvas is not told anything here. `BabylonManager` observes its canvas
   * with a `ResizeObserver` and calls `engine.resize()` from it, so widening the
   * viewport is itself the notification, and `ResizeObserver` delivers at most
   * once per frame — which is the throttling a continuous drag needs, without a
   * second one layered on top.
   */
  const onSidebarResize = React.useCallback((deltaX: number) => {
    const sidebar = sidebarRef.current;
    if (sidebar === null) {
      return;
    }
    const width = sidebar.offsetWidth + deltaX;
    sidebar.style.width = `${width}px`;
    PreviewSidebarStorage.setWidth(width);
  }, []);

  return (
    <div className={styles.previewStep}>
      {unresolved.length > 0 && (
        <Alert severity="warning">
          {unresolved.length} reference
          {unresolved.length === 1 ? "" : "s"} could not be resolved, so the
          model is missing that geometry: {unresolved.join(", ")}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {file === undefined && !resolving && (
        <Alert severity="error">
          The converted model could not be found. The conversion reported
          success, so the file may still be being written — reopen this dialog
          to look again.
        </Alert>
      )}
      <div className={styles.previewBody}>
        {/* Locating the artifact and fetching it are one wait as far as the
            user is concerned — the first is a record lookup they never asked
            for — so they share an indicator rather than handing the panel from
            one spinner to another. */}
        {(isLoading || resolving) && (
          <div className={styles.loading}>
            {/* The wording moves onto the indicator rather than being dropped
                with the caption. A bare `CircularProgress` is a `progressbar`
                with no accessible name, which announces as nothing at all —
                so the wait is still stated, just not drawn. */}
            <CircularProgress
              aria-label="Loading the converted model"
              size="1.5rem"
            />
          </div>
        )}
        {scene && (
          <>
            {/* One sidebar, to the left of the viewport, holding everything
                that edits the preview. It leads the viewport in the DOM so
                reading order and tab order match what is on screen — which is
                why this is source order rather than `order` or `row-reverse`.

                Scenery and viewpoint come first, above the joints: they are a
                fixed handful of fields, so they stay reachable without
                scrolling past a list whose length is the robot's. */}
            <aside className={styles.sidebar} ref={attachSidebar}>
              {sceneConfig && (
                <PreviewSceneSettings
                  camera={sceneConfig.camera}
                  grid={sceneConfig.grid}
                  onCameraChange={setCamera}
                  onGridChange={setGrid}
                />
              )}
              <h2 className={styles.sidebarTitle}>
                Joints ({scene.joints.length})
              </h2>
              {/* Only where there is a draft to edit into. Without one there is
                  nothing to bind to and nothing to save, so a two-way control
                  with one dead side would be offering a mode that is not
                  there. */}
              {onBindingChange !== undefined && (
                <ToggleButtonGroup
                  aria-label="What the joint rows show"
                  className={styles.jointMode}
                  exclusive
                  fullWidth
                  onChange={(_event, next: JointSectionMode | null) => {
                    // MUI reports `null` when the pressed button was already
                    // the active one. There is no "neither" state here — the
                    // section would render as a bare list of joint names — so
                    // that deselection is ignored rather than stored.
                    if (next !== null) {
                      setJointMode(next);
                    }
                  }}
                  size="small"
                  value={jointMode}
                >
                  <ToggleButton value="pose">Joint preview</ToggleButton>
                  <ToggleButton value="bind">Bind to topics</ToggleButton>
                </ToggleButtonGroup>
              )}
              <JointControls
                bindings={bindings}
                joints={scene.joints}
                onBindingChange={onBindingChange}
                mode={jointMode}
                onChange={setJoint}
                orgId={sourceFile?.org_id}
                valueOf={valueOf}
              />
            </aside>
            {/* Between the two panes rather than inside the sidebar, which is
                where the app's other resize handles live: this one scrolls, and
                a handle within it would scroll out of reach with the joints.
                It also puts the handle between the sidebar and the viewport in
                tab order, which is where it is on screen. */}
            <SidebarResizeHandle onResize={onSidebarResize} />
            <BabylonPreview
              modelLoader={modelLoader}
              onManagerChanged={onManagerChanged}
              state={state ?? scene.state}
            />
          </>
        )}
      </div>
    </div>
  );
}
